import React, { useRef, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Button } from 'components/ui/button';
import { Col, Form, Row } from 'react-bootstrap-5';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { fetchTranslation } from '../../../../helpers/i18n';
import { post } from 'helpers/api';
import GoogleLogo from 'logos/google-logo-standalone.png';
import LinkedInLogo from 'logos/LinkedIn-logo-standalone.png';
import HideShowPassword from 'components/form/hide_show_password';
import { PROFILE_OPTIONS, setProfileData } from '../../helpers/preview';

export function Login({
  checkSsoRedirectPath,
  hasGates,
  loginOnly,
  loginPath,
  oauthUrls,
  previewPropsForLogin,
  readonly,
  redirectTo,
  resetPasswordPath,
  onNext
}) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailFound, setEmailFound] = useState();
  const [invalidPassword, setInvalidPassword] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [otpAttempt, setOtpAttempt] = useState('');
  const [invalidOtp, setInvalidOtp] = useState(false);
  const [passwordReset, setPasswordReset] = useState(false);
  const [fetching, setFetching] = useState(false);
  const pendingLogin = useRef(null);

  // A response for credentials that have since changed or been abandoned is ignored.
  const clearPendingLogin = () => {
    pendingLogin.current = null;
    setFetching(false);
  };

  const resetMfa = () => {
    setMfaRequired(false);
    setOtpAttempt('');
    setInvalidOtp(false);
  };

  const resetForm = () => {
    clearPendingLogin();
    setEmailFound(undefined);
    setPasswordReset(false);
    resetMfa();
  };

  // A new password needs a new code, so editing it goes back to the password step.
  const changePassword = (e) => {
    setPassword(e.target.value);
    clearPendingLogin();
    if (mfaRequired) resetMfa();
  };

  const resetPassword = () => {
    post(resetPasswordPath, { email, redirectTo }).then(() => {
      setPasswordReset(true);
    });
  };

  const checkEmail = (e) => {
    e.preventDefault();
    setFetching(true);
    const params = { email, currentUrl: window.location.href, readonly };
    post(checkSsoRedirectPath, params).then((data) => {
      if (data.buildoutLogin) {
        setEmailFound(true);
        if (readonly) setEmail('test_user@testing.com');
      } else if (data.externalLoginPath) {
        window.location = data.externalLoginPath;
      }
    }).catch(() => {
      setEmailFound(false);
    }).finally(() => setFetching(false));
  };

  const login = (e) => {
    e.preventDefault();
    if (pendingLogin.current) return;

    const request = {};
    pendingLogin.current = request;
    setFetching(true);

    post(loginPath, { email, password, readonly, otpAttempt: mfaRequired ? otpAttempt : undefined })
      .then((data) => {
        if (pendingLogin.current !== request) return;

        if (data?.mfaRequired) {
          setInvalidPassword(false);
          setMfaRequired(true);
          return;
        }

        if (readonly) {
          const updatedProfile = {
            [PROFILE_OPTIONS.PROFILE_PRESENT]: true,
            [PROFILE_OPTIONS.ATTRIBUTES]: previewPropsForLogin,
          };

          setProfileData(updatedProfile);
        }

        onNext();
      })
      .catch(() => {
        if (pendingLogin.current !== request) return;

        if (mfaRequired) {
          setInvalidOtp(true);
        } else {
          setInvalidPassword(true);
        }
      })
      .finally(() => {
        if (pendingLogin.current === request) clearPendingLogin();
      });
  };

  if (passwordReset) return (
    <div>
      <div className="text-center">
        <h4>
          {fetchTranslation('password_reset_sent_to', { email })}
        </h4>
        {fetchTranslation('check_email_for_instructions')}
        <div className="mt-3">
          <Button name="back-to-login" variant="ghost" onClick={resetForm}>
            <FontAwesomeIcon className="me-1" icon={faChevronLeft}/>
            {fetchTranslation('back')}
          </Button>
        </div>
      </div>
    </div>
  );

  if (emailFound) return (
    <div>
      <Button name="back-to-login" variant="ghost" onClick={resetForm}>
        <FontAwesomeIcon className="me-1" icon={faChevronLeft}/>
        {fetchTranslation('back')}
      </Button>

      <form onSubmit={login}>
        <h5 className="mb-5 text-center">
          <strong>{fetchTranslation('sign_in')} {!loginOnly && fetchTranslation('to_view_this_document')}</strong>
        </h5>
        <p className="text-center">
          {fetchTranslation('enter_password_for', { email })}
        </p>
        {invalidPassword === true && (
          <p className="text-danger text-center animate__animated animate__fadeIn">
            <FontAwesomeIcon className="me-2" icon={faInfoCircle}/> {fetchTranslation('password_is_incorrect')}
          </p>
        )}

        <Form.Group className="mb-6">
          <Form.Label htmlFor="login-password">{fetchTranslation('password')}</Form.Label>
          <HideShowPassword
            autoFocus
            id="login-password"
            name="password"
            placeholder={fetchTranslation('enter_password')}
            value={password}
            onChange={changePassword}
          />
        </Form.Group>
        {mfaRequired && (
          <Form.Group className="mb-6">
            <Form.Label htmlFor="login-otp-attempt">{fetchTranslation('mfa_code')}</Form.Label>
            {invalidOtp && (
              <p className="text-danger animate__animated animate__fadeIn" name="otp_error">
                <FontAwesomeIcon className="me-2" icon={faInfoCircle}/> {fetchTranslation('mfa_code_is_incorrect')}
              </p>
            )}
            <Form.Control
              autoComplete="one-time-code"
              autoFocus
              id="login-otp-attempt"
              inputMode="numeric"
              maxLength={6}
              name="otp_attempt"
              placeholder={fetchTranslation('enter_mfa_code')}
              required
              value={otpAttempt}
              onChange={e => setOtpAttempt(e.target.value)}
            />
          </Form.Group>
        )}
        <Button className="w-100" disabled={fetching} name="login" type="submit">
          {fetchTranslation('log_in')}
        </Button>
        <div className="text-center mt-3">
          <Button name="forgot-password" variant="ghost" onClick={resetPassword}>
            {fetchTranslation('forgot_password')}
          </Button>
        </div>
      </form>
    </div>
  );

  const canCreate = !hasGates;
  const loginText = canCreate ?
    fetchTranslation('login_or_create_account') :
    fetchTranslation('login_to_buildout_to_access');

  return (
    <div>
      {hasGates && (
        <Button
          className="d-none d-lg-block"
          variant="ghost"
          onClick={() => onNext({ overrideStep: 'create_account' })}
        >
          <FontAwesomeIcon className="me-2" icon={faChevronLeft} size="lg"/>
          {fetchTranslation('back')}
        </Button>
      )}
      <h5 className="mb-5 text-center">
        <strong>{fetchTranslation('sign_in')} {!loginOnly && fetchTranslation('to_view_this_document')}</strong>
      </h5>
      <p className="text-center">
        {loginText}
      </p>
      <form onSubmit={checkEmail}>
        {emailFound === false && (
          <p className="text-danger animate__animated animate__fadeIn" name="email_error">
            <FontAwesomeIcon className="me-2" icon={faInfoCircle}/> {fetchTranslation('couldnt_find_account')}
          </p>
        )}
        <Form.Group className="mb-6">
          <Form.Label>{fetchTranslation('email_address')}</Form.Label>
          <Form.Control
            autoFocus
            className="login-email-field"
            name="email"
            placeholder={fetchTranslation('enter_email_address')}
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
        </Form.Group>
        <Button className="w-100 mb-3" disabled={fetching} name="verify_email" type="submit">
          {fetchTranslation('next')}
        </Button>
      </form>
      {canCreate && (
        <div className="text-center">
          <Button variant="ghost" onClick={() => onNext({ overrideStep: 'create_account' })}>
            {fetchTranslation('create_a_buildout_account')}
          </Button>
        </div>
      )}
      <Row className="align-items-center my-3">
        <Col>
          <hr className="m-0"/>
        </Col>
        <Col xs="auto">{fetchTranslation('or_log_in_with')}</Col>
        <Col>
          <hr className="m-0"/>
        </Col>
      </Row>
      <Row>
        <Col>
          <a
            className="btn btn-outline-neutral border w-100 d-flex align-items-center justify-content-center"
            href={`${oauthUrls.google}&origin=${encodeURIComponent(window.location.href)}`}
          >
            <img className="me-2" src={GoogleLogo} style={{ height: '30px' }}/>
            <span>Google</span>
          </a>
        </Col>
        <Col>
          <a
            className="btn btn-outline-neutral border w-100 d-flex align-items-center justify-content-center"
            href={`${oauthUrls.linkedIn}&origin=${encodeURIComponent(window.location.href)}`}
          >
            <img
              className="me-2"
              src={LinkedInLogo}
              style={{ height: '26px', marginTop: '2px', marginBottom: '2px' }}
            />
            <span>LinkedIn</span>
          </a>
        </Col>
      </Row>
    </div>
  );
}

Login.propTypes = {
  checkSsoRedirectPath: PropTypes.string.isRequired,
  hasGates: PropTypes.bool.isRequired,
  loginOnly: PropTypes.bool,
  loginPath: PropTypes.string.isRequired,
  oauthUrls: PropTypes.shape({
    google: PropTypes.string.isRequired,
    linkedIn: PropTypes.string.isRequired
  }).isRequired,
  previewPropsForLogin: PropTypes.object,
  readonly: PropTypes.bool,
  redirectTo: PropTypes.string.isRequired,
  resetPasswordPath: PropTypes.string.isRequired,
  onNext: PropTypes.func.isRequired
};

export const mapStateToProps = ({ preview, vault }) => (
  { oauthUrls: vault.oauthUrls, previewPropsForLogin: preview.previewPropsForLogin }
);
export default connect(mapStateToProps)(Login);
